import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useIsMobile } from '../../hooks/useIsMobile';
import { createSupplier } from './api';
const CATEGORIES = [
    'Glass',
    'Hardware',
    'Frames',
    'Sealants & Adhesives',
    'Consumables',
    'Tools & Equipment',
    'Other',
];
const PAYMENT_TERMS = ['Net 30', 'Net 60', 'Net 90', 'COD', 'Due on Receipt', 'Other'];
export const SupplierCreate = () => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [form, setForm] = useState({
        name: '',
        status: 'active',
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
    const setAddress = (field, value) => setForm((prev) => ({ ...prev, address: { ...prev.address, [field]: value } }));
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name?.trim()) {
            setSubmitError('Company name is required.');
            return;
        }
        setSubmitting(true);
        setSubmitError(null);
        try {
            const supplier = await createSupplier({
                ...form,
                name: form.name.trim(),
            });
            navigate(`/suppliers/${supplier.id}`);
        }
        catch (e) {
            setSubmitError(e.message ?? 'Failed to create supplier');
            setSubmitting(false);
        }
    };
    const containerStyle = {
        maxWidth: isMobile ? '100%' : '720px',
        margin: '0 auto',
    };
    return (_jsx(Layout, { children: _jsxs("div", { style: containerStyle, children: [_jsxs("div", { style: styles.pageHeader, children: [_jsx("button", { style: styles.backButton, onClick: () => navigate('/suppliers'), children: "\u2190 Back" }), _jsx("h2", { style: styles.heading, children: "New Supplier" })] }), _jsxs("form", { onSubmit: handleSubmit, children: [_jsxs("div", { style: styles.section, children: [_jsx("h3", { style: styles.sectionTitle, children: "Company Info" }), _jsxs("div", { style: isMobile ? styles.fieldStack : styles.fieldRow, children: [_jsxs("div", { style: styles.fieldGrow, children: [_jsxs("label", { style: styles.label, children: ["Company Name ", _jsx("span", { style: styles.req, children: "*" })] }), _jsx("input", { type: "text", style: styles.input, value: form.name, onChange: (e) => set('name', e.target.value), placeholder: "Supplier name", disabled: submitting, required: true })] }), _jsxs("div", { style: styles.fieldFixed, children: [_jsx("label", { style: styles.label, children: "Status" }), _jsxs("select", { style: styles.select, value: form.status, onChange: (e) => set('status', e.target.value), disabled: submitting, children: [_jsx("option", { value: "active", children: "Active" }), _jsx("option", { value: "inactive", children: "Inactive" })] })] })] }), _jsxs("div", { style: isMobile ? styles.fieldStack : styles.fieldRow, children: [_jsxs("div", { style: styles.fieldGrow, children: [_jsx("label", { style: styles.label, children: "Category" }), _jsxs("select", { style: styles.select, value: form.category || '', onChange: (e) => set('category', e.target.value || undefined), disabled: submitting, children: [_jsx("option", { value: "", children: "Select category\u2026" }), CATEGORIES.map((c) => _jsx("option", { value: c, children: c }, c))] })] }), _jsxs("div", { style: styles.fieldGrow, children: [_jsx("label", { style: styles.label, children: "Account Number" }), _jsx("input", { type: "text", style: styles.input, value: form.accountNumber || '', onChange: (e) => set('accountNumber', e.target.value || undefined), placeholder: "Your account # with this supplier", disabled: submitting })] })] }), _jsxs("div", { style: isMobile ? styles.fieldStack : styles.fieldRow, children: [_jsxs("div", { style: styles.fieldGrow, children: [_jsx("label", { style: styles.label, children: "Tax ID" }), _jsx("input", { type: "text", style: styles.input, value: form.taxId || '', onChange: (e) => set('taxId', e.target.value || undefined), placeholder: "EIN / Tax ID", disabled: submitting })] }), _jsxs("div", { style: styles.fieldGrow, children: [_jsx("label", { style: styles.label, children: "Payment Terms" }), _jsxs("select", { style: styles.select, value: form.paymentTerms || '', onChange: (e) => set('paymentTerms', e.target.value || undefined), disabled: submitting, children: [_jsx("option", { value: "", children: "Select terms\u2026" }), PAYMENT_TERMS.map((t) => _jsx("option", { value: t, children: t }, t))] })] })] }), _jsxs("div", { style: isMobile ? styles.fieldStack : styles.fieldRow, children: [_jsxs("div", { style: styles.fieldGrow, children: [_jsx("label", { style: styles.label, children: "Website" }), _jsx("input", { type: "url", style: styles.input, value: form.website || '', onChange: (e) => set('website', e.target.value || undefined), placeholder: "https://\u2026", disabled: submitting })] }), _jsxs("div", { style: styles.fieldFixed, children: [_jsx("label", { style: styles.label, children: "Credit Limit ($)" }), _jsx("input", { type: "number", style: styles.input, value: form.creditLimit ?? '', onChange: (e) => set('creditLimit', e.target.value ? Number(e.target.value) : undefined), placeholder: "0.00", min: "0", step: "0.01", disabled: submitting })] })] })] }), _jsxs("div", { style: styles.section, children: [_jsx("h3", { style: styles.sectionTitle, children: "Contact" }), _jsxs("div", { style: isMobile ? styles.fieldStack : styles.fieldRow, children: [_jsxs("div", { style: styles.fieldGrow, children: [_jsx("label", { style: styles.label, children: "First Name" }), _jsx("input", { type: "text", style: styles.input, value: form.contactFirstName || '', onChange: (e) => set('contactFirstName', e.target.value || undefined), placeholder: "Contact first name", disabled: submitting })] }), _jsxs("div", { style: styles.fieldGrow, children: [_jsx("label", { style: styles.label, children: "Last Name" }), _jsx("input", { type: "text", style: styles.input, value: form.contactLastName || '', onChange: (e) => set('contactLastName', e.target.value || undefined), placeholder: "Contact last name", disabled: submitting })] })] }), _jsxs("div", { style: isMobile ? styles.fieldStack : styles.fieldRow, children: [_jsxs("div", { style: styles.fieldGrow, children: [_jsx("label", { style: styles.label, children: "Phone" }), _jsx("input", { type: "tel", style: styles.input, value: form.phone || '', onChange: (e) => set('phone', e.target.value || undefined), placeholder: "(555) 555-5555", disabled: submitting })] }), _jsxs("div", { style: styles.fieldGrow, children: [_jsx("label", { style: styles.label, children: "AP Email" }), _jsx("input", { type: "email", style: styles.input, value: form.apEmail || '', onChange: (e) => set('apEmail', e.target.value || undefined), placeholder: "accounts@supplier.com", disabled: submitting })] })] }), _jsxs("div", { style: styles.fieldGrow, children: [_jsx("label", { style: styles.label, children: "AR Email" }), _jsx("input", { type: "email", style: styles.input, value: form.arEmail || '', onChange: (e) => set('arEmail', e.target.value || undefined), placeholder: "ar@supplier.com", disabled: submitting })] })] }), _jsxs("div", { style: styles.section, children: [_jsx("h3", { style: styles.sectionTitle, children: "Address" }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: "Street" }), _jsx("input", { type: "text", style: styles.input, value: form.address?.street || '', onChange: (e) => setAddress('street', e.target.value), placeholder: "123 Main St", disabled: submitting })] }), _jsxs("div", { style: isMobile ? styles.fieldStack : styles.fieldRow, children: [_jsxs("div", { style: styles.fieldGrow, children: [_jsx("label", { style: styles.label, children: "City" }), _jsx("input", { type: "text", style: styles.input, value: form.address?.city || '', onChange: (e) => setAddress('city', e.target.value), placeholder: "City", disabled: submitting })] }), _jsxs("div", { style: styles.fieldFixed, children: [_jsx("label", { style: styles.label, children: "State" }), _jsx("input", { type: "text", style: styles.input, value: form.address?.state || '', onChange: (e) => setAddress('state', e.target.value), placeholder: "CA", maxLength: 2, disabled: submitting })] }), _jsxs("div", { style: styles.fieldFixed, children: [_jsx("label", { style: styles.label, children: "ZIP" }), _jsx("input", { type: "text", style: styles.input, value: form.address?.zip || '', onChange: (e) => setAddress('zip', e.target.value), placeholder: "12345", maxLength: 10, disabled: submitting })] })] })] }), _jsxs("div", { style: styles.section, children: [_jsx("h3", { style: styles.sectionTitle, children: "Notes" }), _jsx("textarea", { style: styles.textarea, value: form.notes || '', onChange: (e) => set('notes', e.target.value || undefined), placeholder: "General notes about this supplier\u2026", rows: 4, disabled: submitting })] }), submitError && _jsx("div", { style: styles.errorBanner, children: submitError }), _jsxs("div", { style: styles.actions, children: [_jsx("button", { type: "button", style: styles.cancelButton, onClick: () => navigate('/suppliers'), disabled: submitting, children: "Cancel" }), _jsx("button", { type: "submit", style: { ...styles.submitButton, opacity: submitting ? 0.6 : 1 }, disabled: submitting, children: submitting ? 'Creating…' : 'Create Supplier' })] })] })] }) }));
};
const styles = {
    pageHeader: { marginBottom: '24px' },
    backButton: {
        background: 'none',
        border: 'none',
        color: '#0071e3',
        fontSize: '15px',
        fontWeight: '500',
        cursor: 'pointer',
        padding: '0 0 8px 0',
        display: 'block',
        minHeight: '44px',
    },
    heading: {
        fontSize: '34px',
        fontWeight: '700',
        margin: 0,
        color: '#1d1d1f',
        letterSpacing: '-0.5px',
    },
    section: {
        backgroundColor: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '16px',
        padding: '20px 24px',
        marginBottom: '16px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
    },
    sectionTitle: {
        fontSize: '13px',
        fontWeight: '700',
        color: '#6e6e73',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        margin: '0 0 16px 0',
    },
    field: { marginBottom: '14px' },
    fieldRow: { display: 'flex', gap: '12px', marginBottom: '14px', alignItems: 'flex-start' },
    fieldStack: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '14px' },
    fieldGrow: { flex: 1, minWidth: 0 },
    fieldFixed: { width: '140px', flexShrink: 0 },
    label: {
        display: 'block',
        fontSize: '13px',
        fontWeight: '600',
        color: '#3c3c43',
        marginBottom: '6px',
        letterSpacing: '-0.1px',
    },
    req: { color: '#c0392b' },
    input: {
        width: '100%',
        padding: '10px 14px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        fontSize: '15px',
        color: '#1d1d1f',
        outline: 'none',
        boxSizing: 'border-box',
        minHeight: '44px',
    },
    select: {
        width: '100%',
        padding: '10px 14px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        fontSize: '15px',
        color: '#1d1d1f',
        outline: 'none',
        boxSizing: 'border-box',
        minHeight: '44px',
        cursor: 'pointer',
    },
    textarea: {
        width: '100%',
        padding: '10px 14px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        fontSize: '15px',
        color: '#1d1d1f',
        outline: 'none',
        resize: 'vertical',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
        minHeight: '100px',
    },
    errorBanner: {
        padding: '12px 16px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        border: '1px solid rgba(255,59,48,0.3)',
        borderRadius: '10px',
        color: '#c0392b',
        fontSize: '14px',
        marginBottom: '16px',
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '8px',
        marginBottom: '32px',
    },
    cancelButton: {
        padding: '12px 24px',
        backgroundColor: 'rgba(0,0,0,0.05)',
        color: '#3c3c43',
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: '980px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        minHeight: '44px',
    },
    submitButton: {
        padding: '12px 24px',
        backgroundColor: '#0071e3',
        color: '#fff',
        border: 'none',
        borderRadius: '980px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        minHeight: '44px',
    },
};
//# sourceMappingURL=SupplierCreate.js.map